import interactionResponse from './interaction-response';

// This function takes an array of tasks and runs them one at a time, yielding to the main thread between each task.
export const yieldTasks = async (tasks: (() => void)[]) => {
    // Loop over the tasks:
    while (tasks.length > 0) {
        // Shift the first task off the tasks array:
        const task = tasks.shift();

        if (task) {
            // Run the task:
            task();

            // Allow time for paint to reduce INP (Interaction to Next Paint)
            await interactionResponse();
        }
    }
};

// Upper bound on the post-load idle wait for deferred vendor work, so a main thread
// that never goes idle delays the vendors (and the attribution they carry) by
// seconds, not forever.
export const DEFERRED_VENDOR_IDLE_TIMEOUT_MS = 4000;

// A page whose `load` event never fires (a hung subresource, a stream that never
// closes) must not keep deferred work from ever running.
const LOAD_EVENT_FALLBACK_MS = 15_000;

/**
 * Runs `task` in the next idle period, or after `timeout` if the main thread never goes idle. Without
 * `requestIdleCallback` (Safari) nothing reports idle, so it waits `fallbackDelay`, which defaults to `timeout`.
 * Returns a function that cancels it.
 */
export const requestIdle = (
    task: () => void,
    { timeout, fallbackDelay = timeout }: { timeout: number; fallbackDelay?: number },
): (() => void) => {
    if (typeof window === 'undefined') return () => {};
    if (typeof window.requestIdleCallback === 'function') {
        const handle = window.requestIdleCallback(() => task(), { timeout });
        return () => window.cancelIdleCallback(handle);
    }
    const handle = window.setTimeout(task, fallbackDelay);
    return () => window.clearTimeout(handle);
};

/**
 * Runs `task` once, after the window `load` event and the next idle period —
 * the earliest point that reliably sits past LCP. For work that must not compete
 * with first render (vendor scripts, analytics SDKs). `timeout` bounds the idle
 * wait so a busy main thread delays the task rather than starving it. Returns a
 * function that cancels it.
 */
export const runAfterLoadAndIdle = (task: () => void, { timeout }: { timeout: number }): (() => void) => {
    if (typeof window === 'undefined') return () => {};

    // Past `load`, a macrotask is near enough to idle where nothing reports it.
    const idle = { timeout, fallbackDelay: 0 };
    if (document.readyState === 'complete') return requestIdle(task, idle);

    let cancelIdle: (() => void) | undefined;
    // Whichever of the two fires first cancels the other.
    const onLoadOrFallback = (): void => {
        window.removeEventListener('load', onLoadOrFallback);
        window.clearTimeout(fallbackHandle);
        cancelIdle = requestIdle(task, idle);
    };
    const fallbackHandle = window.setTimeout(onLoadOrFallback, LOAD_EVENT_FALLBACK_MS);
    window.addEventListener('load', onLoadOrFallback, { once: true });
    return () => {
        window.removeEventListener('load', onLoadOrFallback);
        window.clearTimeout(fallbackHandle);
        cancelIdle?.();
    };
};

// Input only: a programmatic scroll (a carousel's autoplay, scroll restoration) is not the visitor arriving.
const FIRST_INTERACTION_EVENTS = ['pointerdown', 'touchstart', 'keydown', 'wheel'] as const;

/**
 * Runs `task` once, on the visitor's first tap, key press or wheel after the window `load` event, or `timeout`
 * after `load`, whichever comes first. An interaction while the page is still loading runs it at `load`, so it
 * never competes with the page's own resources. For scripts that only observe the visitor and may wait past the
 * first seconds entirely. After an interaction it waits for the next frame, so it never delays the response.
 * Returns a function that cancels it.
 */
export const runOnFirstInteractionOrAfterLoad = (task: () => void, { timeout }: { timeout: number }): (() => void) => {
    if (typeof window === 'undefined') return () => {};

    let settled = false;
    let loaded = false;
    let interacted = false;
    let loadFallbackHandle: number | undefined;
    let timeoutHandle: number | undefined;
    const cancel = (): void => {
        settled = true;
        for (const type of FIRST_INTERACTION_EVENTS) window.removeEventListener(type, onInteraction, true);
        window.removeEventListener('load', onLoad);
        window.clearTimeout(loadFallbackHandle);
        window.clearTimeout(timeoutHandle);
    };
    const runAfterFrame = (): void => {
        if (settled) return;
        cancel();
        void interactionResponse().then(task);
    };
    const onInteraction = (): void => {
        if (loaded) runAfterFrame();
        else interacted = true;
    };
    const onLoad = (): void => {
        window.removeEventListener('load', onLoad);
        window.clearTimeout(loadFallbackHandle);
        loaded = true;
        if (interacted) {
            runAfterFrame();
            return;
        }
        timeoutHandle = window.setTimeout(() => {
            if (settled) return;
            cancel();
            task();
        }, timeout);
    };

    for (const type of FIRST_INTERACTION_EVENTS) {
        window.addEventListener(type, onInteraction, { capture: true, passive: true });
    }
    if (document.readyState === 'complete') {
        onLoad();
    } else {
        loadFallbackHandle = window.setTimeout(onLoad, LOAD_EVENT_FALLBACK_MS);
        window.addEventListener('load', onLoad, { once: true });
    }
    return cancel;
};
